const projectSettingsPath = '/project-settings';
const workspaceProjectSettingsPath = '/workspaces';
export const missingProjectRedirectPath = '/execution-runs';
export const missingProjectRedirectMessage = 'Redirecting to execution runs...';
export const projectCsvMappingAnchorId = 'xlsx-csv-mapping';
export const projectExecutionRunThresholdsAnchorId = 'execution-run-metric-thresholds';
export const projectExecutionRunThresholdMetricAnchorIds = {
  passRate: `${projectExecutionRunThresholdsAnchorId}-success-rate`,
  frequency: `${projectExecutionRunThresholdsAnchorId}-frequency`,
  avgDuration: `${projectExecutionRunThresholdsAnchorId}-average-duration`,
  flakeScore: `${projectExecutionRunThresholdsAnchorId}-flake-score`,
} as const;
const csvMappingHash = `#${projectCsvMappingAnchorId}`;
const executionRunThresholdsHash = `#${projectExecutionRunThresholdsAnchorId}`;
const projectIdParam = 'projectId';

export const buildProjectSettingsHref = (projectId: string): string => {
  const searchParams = new URLSearchParams({ [projectIdParam]: projectId });
  return `${projectSettingsPath}?${searchParams.toString()}`;
};

export const buildWorkspaceProjectSettingsHref = ({
  workspaceSlug,
  projectSlug,
}: {
  workspaceSlug: string;
  projectSlug: string;
}): string =>
  `${workspaceProjectSettingsPath}/${encodeURIComponent(workspaceSlug)}/projects/${encodeURIComponent(
    projectSlug,
  )}/settings`;

export const buildProjectSettingsHrefForProject = ({
  projectId,
  projectSlug,
  workspaceSlug,
}: {
  projectId: string;
  projectSlug: string;
  workspaceSlug?: string | null;
}): string =>
  workspaceSlug
    ? buildWorkspaceProjectSettingsHref({ workspaceSlug, projectSlug })
    : buildProjectSettingsHref(projectId);

export const buildProjectCsvMappingSettingsHref = (projectId: string): string => {
  return `${buildProjectSettingsHref(projectId)}${csvMappingHash}`;
};

export const buildProjectCsvMappingSettingsHrefForProject = (input: {
  projectId: string;
  projectSlug: string;
  workspaceSlug?: string | null;
}): string => `${buildProjectSettingsHrefForProject(input)}${csvMappingHash}`;

export type ProjectExecutionRunThresholdMetricKey =
  keyof typeof projectExecutionRunThresholdMetricAnchorIds;

export const buildProjectExecutionRunThresholdsSettingsHref = (
  projectId: string,
  metricKey?: ProjectExecutionRunThresholdMetricKey,
): string => {
  const hash = metricKey
    ? `#${projectExecutionRunThresholdMetricAnchorIds[metricKey]}`
    : executionRunThresholdsHash;
  return `${buildProjectSettingsHref(projectId)}${hash}`;
};

export const buildProjectExecutionRunThresholdMetricSettingsHref = (
  href: string,
  metricKey?: ProjectExecutionRunThresholdMetricKey,
): string => {
  if (!metricKey) return href;
  const hash = `#${projectExecutionRunThresholdMetricAnchorIds[metricKey]}`;
  const hashIndex = href.indexOf('#');
  return hashIndex === -1 ? `${href}${hash}` : `${href.slice(0, hashIndex)}${hash}`;
};

export const resolveProjectSettingsProjectId = (search: string): string | null => {
  const searchParams = new URLSearchParams(search);
  const projectId = searchParams.get(projectIdParam)?.trim() ?? '';
  return projectId.length > 0 ? projectId : null;
};

export const resolveProjectSettingsTabFromHash = (hash: string): 'test' | 'operations' | null => {
  if (hash === `#${projectCsvMappingAnchorId}`) {
    return 'test';
  }
  if (
    hash === `#${projectExecutionRunThresholdsAnchorId}` ||
    Object.values(projectExecutionRunThresholdMetricAnchorIds).some(
      (anchorId) => hash === `#${anchorId}`,
    )
  ) {
    return 'test';
  }
  return null;
};
